import React, { useState, useEffect } from 'react';
import api, { getApiOrigin } from '../services/api';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface HeroSectionProps {
  backgroundImage?: string;
  title?: string;
  subtitle?: string;
  showTextByDefault?: boolean;
}

interface HeroTextConfig {
  title: string;
  subtitle: string;
  show_text_default: boolean;
  text_x: number;
  text_y: number;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  backgroundImage,
  title = "Desvende os Mistérios do Futuro",
  subtitle = "Consultas de tarot personalizadas com interpretação por inteligência artificial. Descubra insights profundos sobre amor, saúde e prosperidade.",
  showTextByDefault = true
}) => {
  const [showText, setShowText] = useState(showTextByDefault);
  const [currentBackgroundImage, setCurrentBackgroundImage] = useState(backgroundImage);
  const [textConfig, setTextConfig] = useState<HeroTextConfig>({
    title: title,
    subtitle: subtitle,
    show_text_default: showTextByDefault,
    text_x: 50,
    text_y: 50,
  });

  // Carregar configurações do backend
  useEffect(() => {
    const loadHeroConfig = async () => {
      try {
        // Carregar imagem de fundo
        const imageResponse = await api.get('/hero/background');
        const imageData = imageResponse.data;
        
        if (imageData?.url) {
          const url: string = imageData.url;
          if (url.startsWith('http://') || url.startsWith('https://')) {
            setCurrentBackgroundImage(url);
          } else {
            const origin = getApiOrigin();
            setCurrentBackgroundImage(`${origin}${url}`);
          }
        } else {
          setCurrentBackgroundImage('');
        }

        // Carregar configurações de texto
        const textResponse = await api.get('/hero/text-config');
        const textData = textResponse.data;
        
        const newTextConfig = {
          title: textData.title || title,
          subtitle: textData.subtitle || subtitle,
          show_text_default: textData.show_text_default !== undefined ? textData.show_text_default : showTextByDefault,
          text_x: textData.text_x ?? 50,
          text_y: textData.text_y ?? 50,
        };
        
        setTextConfig(newTextConfig);
        setShowText(newTextConfig.show_text_default);
        
      } catch (error) {
        console.error('Erro ao carregar configurações do hero:', error);
        // Em caso de erro, não usar imagem
        setCurrentBackgroundImage('');
        setTextConfig({
          title: title,
          subtitle: subtitle,
          show_text_default: showTextByDefault,
          text_x: 50,
          text_y: 50,
        });
        setShowText(showTextByDefault);
      }
    };

    loadHeroConfig();
  }, [backgroundImage, title, subtitle, showTextByDefault]);

  return (
    <section className="relative w-full min-h-screen overflow-hidden">
      {/* Imagem de fundo que ocupa toda a largura - apenas se houver upload */}
      {currentBackgroundImage && (
        <div className="absolute inset-0 w-full h-full">
          <img 
            src={currentBackgroundImage}
            alt="Cartas de Tarot Místicas" 
            className="w-full h-full object-cover"
            onError={() => {
              // Se a imagem falhar, remover ela
              setCurrentBackgroundImage('');
            }}
          />
          {/* Overlay escuro para melhor legibilidade do texto */}
          <div className={`absolute inset-0 transition-all duration-500 ${
            showText ? 'bg-black/50' : 'bg-black/20'
          }`}></div>
        </div>
      )}
      
      {/* Fundo gradiente quando não há imagem */}
      {!currentBackgroundImage && (
        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-mystical-900 via-mystical-800 to-mystical-900"></div>
      )}

      {/* Degradê de fade no bottom para transição suave (controlado por CSS variável) */}
      <div
        className="pointer-events-none absolute bottom-0 inset-x-0 h-24 sm:h-32"
        style={{
          backgroundImage: `linear-gradient(to bottom, transparent, var(--hero-bottom-fade-to))`,
        }}
        aria-hidden="true"
      />
      
      {/* Botão para alternar visibilidade do texto */}
      <button
        onClick={() => setShowText(!showText)}
        className="absolute top-6 right-6 z-20 p-3 bg-white/10 backdrop-blur-sm rounded-full text-white hover:bg-white/20 transition-all duration-300 border border-white/20"
        title={showText ? 'Ocultar texto' : 'Mostrar texto'}
      >
        {showText ? (
          <EyeSlashIcon className="w-6 h-6" />
        ) : (
          <EyeIcon className="w-6 h-6" />
        )}
      </button>
      
      {/* Conteúdo de texto posicionado por X/Y */}
      <div
        className={`absolute z-10 transition-all duration-500 ${showText ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        style={{
          left: `${textConfig.text_x}%`,
          top: `${textConfig.text_y}%`,
          transform: 'translate(-50%, -50%)',
          maxWidth: '600px',
          width: '90%',
          textAlign: 'center',
        }}
      >
        <h2 className="text-5xl md:text-6xl theme-font-heading font-bold text-white mb-6 leading-tight drop-shadow-2xl">
          {textConfig.title.includes('Futuro') ? (
            <>
              Desvende os Mistérios do
              <span className="bg-gradient-to-r from-mystical-400 to-gold-400 bg-clip-text text-transparent font-extrabold"> Futuro</span>
            </>
          ) : (
            textConfig.title
          )}
        </h2>
        <p className="text-xl text-white/90 mb-8 theme-font-primary leading-relaxed drop-shadow-lg">
          {textConfig.subtitle}
        </p>
        
        <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
          <Link 
            to="/register" 
            className="inline-block px-8 py-4 theme-btn-primary text-lg font-semibold shadow-2xl backdrop-blur-sm"
          >
            Começar Consulta Gratuita
          </Link>
          <Link 
            to="/login" 
            className="inline-block px-8 py-4 border-2 border-white/30 text-white rounded-full text-lg font-semibold hover:bg-white/10 theme-transition transform hover:scale-105 backdrop-blur-sm"
          >
            Já tenho conta
          </Link>
        </div>
      </div>
      
      {/* Floating cards animation - apenas quando há imagem de fundo */}
      {currentBackgroundImage && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
          <div className="absolute top-20 left-10 w-16 h-24 bg-gradient-to-br from-mystical-600/30 to-mystical-800/30 rounded-lg shadow-2xl transform rotate-12 animate-pulse backdrop-blur-sm"></div>
          <div className="absolute top-40 right-20 w-12 h-18 bg-gradient-to-br from-gold-600/30 to-gold-800/30 rounded-lg shadow-2xl transform -rotate-6 animate-pulse backdrop-blur-sm" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-40 left-20 w-14 h-20 bg-gradient-to-br from-mystical-500/30 to-mystical-700/30 rounded-lg shadow-2xl transform rotate-6 animate-pulse backdrop-blur-sm" style={{ animationDelay: '2s' }}></div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;